import { useCallback, useEffect, useRef, useState, type MutableRefObject, type Dispatch } from "react";
import {
  CHAT_PAGE_SIZE,
  listOlderMessagesForPeer,
  listRecentMessagesForPeer,
  markPeerMessagesSeen,
  saveMessage,
  type MessageRow,
} from "../../../lib/db";
import {
  apiFetchMessageHistory,
  apiFetchVaultMessages,
  apiFetchWorkspaceTopicMessages,
  getStoredJwt,
  type ServerMessageRow,
} from "../../../lib/authApi";
import {
  serverWorkspaceMessageToRow,
  workspaceReactionsMapFromServerMessages,
} from "../../../lib/workspaceTopicMessages";
import { newFrameId } from "../../../lib/frame";
import type { IdentityKeys } from "../../../lib/crypto";
import { deriveVaultKey } from "../../../lib/crypto";
import { vaultServerRowsToMessageRows } from "../../../lib/vaultMessages";
import {
  dmPeerIdFromConversation,
  vaultIdFromConversation,
  type ChatAction,
  type ChatState,
  type ConversationRef,
} from "../../state/chatState";

type DecodeFn = (row: ServerMessageRow) => Promise<MessageRow | null>;

export function useChatMessageLoading(
  chatState: ChatState,
  dispatchChat: Dispatch<ChatAction>,
  refreshUnreadCounts: () => Promise<void>,
  sendRealtimeCommand: (type: string, payload: Record<string, unknown>, id?: string) => boolean,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
  keysRef: MutableRefObject<IdentityKeys | null>,
  decodeServerMessageRow: DecodeFn,
) {
  const [loadingOlder, setLoadingOlder] = useState(false);
  const messagesHeadIdRef = useRef<string | undefined>(undefined);
  const chatMessagesRef = useRef<MessageRow[]>([]);

  useEffect(() => {
    messagesHeadIdRef.current = chatState.messages[0]?.id;
  }, [chatState.messages]);

  useEffect(() => {
    chatMessagesRef.current = chatState.messages;
  }, [chatState.messages]);

  const loadPeerMessagesIntoState = useCallback(
    async (peerId: string, options?: { keepLoadingWhenEmpty?: boolean }) => {
      const rows = await listRecentMessagesForPeer(peerId, CHAT_PAGE_SIZE);
      const prev = chatMessagesRef.current;
      const prevSamePeer = prev.length > 0 && prev.every((m) => m.peerId === peerId);
      let merged: MessageRow[];
      if (prevSamePeer) {
        const byId = new Map<string, MessageRow>();
        for (const m of rows) byId.set(m.id, m);
        for (const m of prev) byId.set(m.id, m);
        merged = [...byId.values()].sort((a, b) =>
          a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
        );
      } else {
        merged = rows;
      }

      let hasMoreOlder = false;
      if (merged.length > 0) {
        const oldest = merged[0]!;
        const probe = await listOlderMessagesForPeer(peerId, oldest.created_at, oldest.id, 1);
        hasMoreOlder = probe.length > 0;
      }
      const seenIds = await markPeerMessagesSeen(peerId);
      if (seenIds.length > 0) {
        for (const messageId of seenIds) {
          sendRealtimeCommand("msg.read", { message_id: messageId }, newFrameId());
        }
      }
      const seenSet = new Set(seenIds);
      const nextRows = merged.map((message) =>
        seenSet.has(message.id) ? { ...message, seenLocal: true } : message,
      );
      if (dmPeerIdFromConversation(selectedConversationRef.current) === peerId) {
        dispatchChat({
          type: "set-messages",
          messages: nextRows,
          hasMoreOlder,
          conversationLoading: options?.keepLoadingWhenEmpty === true && nextRows.length === 0,
        });
      }
      await refreshUnreadCounts();
    },
    [dispatchChat, refreshUnreadCounts, selectedConversationRef, sendRealtimeCommand],
  );

  const loadOlderChatMessages = useCallback(async () => {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try {
      const ref = chatState.selectedConversation;
      if (ref?.kind === "workspace-topic") {
        const oldest = chatState.messages[0];
        if (!oldest || !chatState.chatHasMoreOlder) return;
        const anchorOldestId = oldest.id;
        const { workspaceId, topicId } = ref;
        try {
          const { messages } = await apiFetchWorkspaceTopicMessages(
            workspaceId,
            topicId,
            CHAT_PAGE_SIZE,
            oldest.created_at,
            oldest.id,
          );
          if (selectedConversationRef.current?.kind !== "workspace-topic") return;
          if (
            selectedConversationRef.current.workspaceId !== workspaceId ||
            selectedConversationRef.current.topicId !== topicId
          ) {
            return;
          }
          if (messagesHeadIdRef.current !== anchorOldestId) return;
          const older = messages.map((m) => serverWorkspaceMessageToRow(workspaceId, topicId, m));
          let hasMore = false;
          if (messages.length === 0) {
            hasMore = false;
          } else if (messages.length < CHAT_PAGE_SIZE) {
            hasMore = false;
          } else {
            const probe = await apiFetchWorkspaceTopicMessages(
              workspaceId,
              topicId,
              1,
              older[0].created_at,
              older[0].id,
            );
            if (selectedConversationRef.current?.kind !== "workspace-topic") return;
            if (
              selectedConversationRef.current.workspaceId !== workspaceId ||
              selectedConversationRef.current.topicId !== topicId
            ) {
              return;
            }
            if (messagesHeadIdRef.current !== anchorOldestId) return;
            hasMore = probe.messages.length > 0;
          }
          if (selectedConversationRef.current?.kind !== "workspace-topic") return;
          if (
            selectedConversationRef.current.workspaceId !== workspaceId ||
            selectedConversationRef.current.topicId !== topicId
          ) {
            return;
          }
          if (messagesHeadIdRef.current !== anchorOldestId) return;
          dispatchChat({
            type: "prepend-messages",
            messages: older,
            hasMoreOlder: hasMore,
            workspaceReactionsMerge: workspaceReactionsMapFromServerMessages(messages),
          });
        } catch {
          dispatchChat({ type: "set-error", error: "Не удалось подгрузить старые сообщения" });
        }
        return;
      }

      const vId = vaultIdFromConversation(ref);
      if (vId) {
        const oldest = chatState.messages[0];
        const keys = keysRef.current;
        if (!oldest || !chatState.chatHasMoreOlder || !keys) return;
        const anchorOldestId = oldest.id;
        const vaultKey = deriveVaultKey(keys.secretKeyX);
        try {
          const { messages: raw } = await apiFetchVaultMessages(
            vId,
            CHAT_PAGE_SIZE,
            oldest.created_at,
            oldest.id,
          );
          if (vaultIdFromConversation(selectedConversationRef.current) !== vId) return;
          if (messagesHeadIdRef.current !== anchorOldestId) return;
          const older = await vaultServerRowsToMessageRows(vId, keys.userId, raw, vaultKey);
          let hasMore = false;
          if (raw.length === 0) {
            hasMore = false;
          } else if (raw.length < CHAT_PAGE_SIZE) {
            hasMore = false;
          } else {
            const probe = await apiFetchVaultMessages(vId, 1, older[0].created_at, older[0].id);
            if (vaultIdFromConversation(selectedConversationRef.current) !== vId) return;
            if (messagesHeadIdRef.current !== anchorOldestId) return;
            hasMore = probe.messages.length > 0;
          }
          if (vaultIdFromConversation(selectedConversationRef.current) !== vId) return;
          if (messagesHeadIdRef.current !== anchorOldestId) return;
          dispatchChat({
            type: "prepend-messages",
            messages: older,
            hasMoreOlder: hasMore,
          });
        } catch {
          dispatchChat({ type: "set-error", error: "Не удалось подгрузить старые сообщения" });
        }
        return;
      }

      const peerId = dmPeerIdFromConversation(ref);
      const oldestDm = chatState.messages[0];
      if (!peerId || !oldestDm || !chatState.chatHasMoreOlder) return;
      const anchorOldestId = oldestDm.id;
      let older = await listOlderMessagesForPeer(peerId, oldestDm.created_at, oldestDm.id, CHAT_PAGE_SIZE);
      if (dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
      if (messagesHeadIdRef.current !== anchorOldestId) return;
      if (older.length === 0 && getStoredJwt()) {
        const activeKeys = keysRef.current;
        try {
          const { messages: fromServer } = await apiFetchMessageHistory(
            peerId,
            CHAT_PAGE_SIZE,
            oldestDm.created_at,
            oldestDm.id,
          );
          for (const row of fromServer) {
            const decoded = await decodeServerMessageRow(row);
            if (!decoded) continue;
            await saveMessage(decoded);
            if (
              activeKeys &&
              row.to_id === activeKeys.userId &&
              dmPeerIdFromConversation(selectedConversationRef.current) === peerId
            ) {
              sendRealtimeCommand("msg.delivery_ack", { message_id: row.id }, newFrameId());
              sendRealtimeCommand("msg.read", { message_id: row.id }, newFrameId());
            }
          }
          if (dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
          if (messagesHeadIdRef.current !== anchorOldestId) return;
          older = await listOlderMessagesForPeer(peerId, oldestDm.created_at, oldestDm.id, CHAT_PAGE_SIZE);
        } catch {
          /* offline */
        }
      }
      if (dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
      if (messagesHeadIdRef.current !== anchorOldestId) return;
      let hasMore = false;
      if (older.length === 0) {
        hasMore = false;
      } else if (older.length < CHAT_PAGE_SIZE) {
        hasMore = false;
      } else {
        const probe = await listOlderMessagesForPeer(peerId, older[0].created_at, older[0].id, 1);
        hasMore = probe.length > 0;
      }
      if (dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
      if (messagesHeadIdRef.current !== anchorOldestId) return;
      dispatchChat({ type: "prepend-messages", messages: older, hasMoreOlder: hasMore });
    } finally {
      setLoadingOlder(false);
    }
  }, [
    chatState.chatHasMoreOlder,
    chatState.messages,
    chatState.selectedConversation,
    decodeServerMessageRow,
    dispatchChat,
    keysRef,
    loadingOlder,
    selectedConversationRef,
    sendRealtimeCommand,
    getStoredJwt,
  ]);

  return {
    loadingOlder,
    loadPeerMessagesIntoState,
    loadOlderChatMessages,
  };
}
